/**
 * Relatie Detail/Edit Pagina
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { relationsService, Relation } from '../../services/relations.service';
import { useAuth } from '../../context/AuthContext';
import { botenService, BootDetail } from '../../services/boten.service';
import { bootEigenarenService } from '../../services/bootEigenaren.service';
import apiFetch from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import RelationForm from './RelationForm';
import './Relations.css';

interface RelationDetailProps {
  relationId: number;
}

// Bepaal basename voor portaal URL
const getBasename = () => '';

interface PortaalBezoek {
  id: number;
  begonnen_op: string;
  laatste_actie: string | null;
  aantal_aanroepen: number;
  ip: string | null;
  browser: string | null;
}

interface PortaalTokenInfo {
  has_token: boolean;
  token?: string;
  portaal_url?: string;
  laatste_toegang?: number;
  aantal_keer_bekeken?: number;
}

/* De contactpersoon staat in vier losse velden (aanhef, voornaam, tussenvoegsel,
   achternaam). Het oude losse veld `contactpersoon` wordt niet meer gevuld door het
   formulier, maar oudere records gebruiken het nog - vandaar de terugval. */
const contactpersoonNaam = (r: Relation): string => {
  const delen = [
    r.contactpersoon_aanhef,
    r.contactpersoon_voornaam,
    r.contactpersoon_tussenvoegsel,
    r.contactpersoon_achternaam,
  ].filter(Boolean);
  return delen.length ? delen.join(' ') : (r.contactpersoon || '');
};

const RelationDetail = ({ relationId }: RelationDetailProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [relation, setRelation] = useState<Relation | null>(null);
  const [boten, setBoten] = useState<BootDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [botenLoading, setBotenLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteBoot, setConfirmDeleteBoot] = useState<{ id: number; naam: string } | null>(null);
  const [portaalTokenInfo, setPortaalTokenInfo] = useState<PortaalTokenInfo | null>(null);
  const [bezoeken, setBezoeken] = useState<PortaalBezoek[]>([]);
  const [bezoekenOpen, setBezoekenOpen] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const loadRelation = useCallback(async () => {
    try {
      setLoading(true);
      const data = await relationsService.getById(relationId);
      setRelation(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [relationId]);

  const loadBoten = useCallback(async () => {
    try {
      setBotenLoading(true);
      const data = await botenService.getAll({ eigenaar_id: relationId });
      setBoten(data);
    } catch (err: any) {
      // Silently fail - boats list will remain empty
      // Main relation data is still shown
    } finally {
      setBotenLoading(false);
    }
  }, [relationId]);

  const loadPortaalToken = useCallback(async () => {
    try {
      const info = await apiFetch(`/api/v1/klant-portaal/token/info/${relationId}`);
      setPortaalTokenInfo(info);
      if (info?.has_token) {
        setBezoeken(await apiFetch(`/api/v1/klant-portaal/token/bezoeken/${relationId}`));
      }
    } catch (err: any) {
      // Silently fail - portal info is optional
    }
  }, [relationId]);

  const handleGenerateToken = async () => {
    try {
      setTokenLoading(true);
      const result = await apiFetch(`/api/v1/klant-portaal/token/generate/${relationId}`, {
        method: 'POST'
      });
      setPortaalTokenInfo({
        has_token: true,
        token: result.token,
        portaal_url: result.portaal_url,
        aantal_keer_bekeken: 0
      });
    } catch (err: any) {
      setError(err.message || 'Fout bij genereren portaal link');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!window.confirm('Weet je zeker dat je de portaal link wilt intrekken? De klant kan dan niet meer inloggen.')) {
      return;
    }

    try {
      setTokenLoading(true);
      await apiFetch(`/api/v1/klant-portaal/token/${relationId}`, {
        method: 'DELETE'
      });
      setPortaalTokenInfo({ has_token: false });
    } catch (err: any) {
      setError(err.message || 'Fout bij intrekken portaal link');
    } finally {
      setTokenLoading(false);
    }
  };

  const copyPortaalLink = () => {
    if (!portaalTokenInfo?.portaal_url) return;

    const fullUrl = `${window.location.origin}${getBasename()}${portaalTokenInfo.portaal_url}`;
    navigator.clipboard.writeText(fullUrl);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  const handleToggleBootEigenaar = async (bootId: number, isHuidigeEigenaar: boolean) => {
    if (isHuidigeEigenaar) {
      // Boot is actief voor deze eigenaar - maak passief door aan "Onbekend" toe te wijzen
      try {
        // ID 22760 is de "Onbekend" eigenaar
        await botenService.update(bootId, { eigenaar_id: 22760 });
        // Reload boten lijst om de wijziging te tonen
        loadBoten();
      } catch (err: any) {
        setError(err.message || 'Fout bij wijzigen eigenaar');
      }
    } else {
      // Boot is passief voor deze eigenaar - reactiveer door eigenaar terug te zetten
      try {
        await botenService.update(bootId, { eigenaar_id: relationId });
        // Reload boten lijst om de wijziging te tonen
        loadBoten();
      } catch (err: any) {
        setError(err.message || 'Fout bij wijzigen eigenaar');
      }
    }
  };

  const handleVerwijderBootClick = (bootId: number, bootNaam: string) => {
    setConfirmDeleteBoot({ id: bootId, naam: bootNaam });
  };

  const handleVerwijderBootConfirm = async () => {
    if (!confirmDeleteBoot) return;

    try {
      // Verwijder de boot-eigenaar koppeling
      await bootEigenarenService.remove(confirmDeleteBoot.id, relationId);
      setConfirmDeleteBoot(null);
      loadBoten();
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen boot van klant');
      setConfirmDeleteBoot(null);
    }
  };

  const handleVerwijderBootCancel = () => {
    setConfirmDeleteBoot(null);
  };

  useEffect(() => {
    loadRelation();
    loadBoten();
    loadPortaalToken();
  }, [loadRelation, loadBoten, loadPortaalToken]);

  // Helper: Bepaal display naam
  const getDisplayNaam = () => {
    if (!relation) return '';

    if (relation.bedrijfsnaam === 'Particulier' && relation.achternaam) {
      const naamParts = [relation.voornaam, relation.tussenvoegsel, relation.achternaam].filter(Boolean);
      return naamParts.join(' ');
    }
    return relation.bedrijfsnaam;
  };

  // Helper: Bepaal of het een particulier is
  const isParticulier = () => {
    return relation?.bedrijfsnaam === 'Particulier';
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  if (error || !relation) {
    return (
      <div className="error-page">
        <h1>Fout</h1>
        <p>{error || 'Relatie niet gevonden'}</p>
      </div>
    );
  }

  if (isEditing) {
    return <RelationForm relationId={relationId} />;
  }

  return (
    <div className="relation-detail">
      <div className="page-header">
        <h1>{getDisplayNaam()}</h1>
        {hasPermission('relaties_bewerken') && (
          <button
            className="btn-primary"
            onClick={() => setIsEditing(true)}
          >
            Bewerken
          </button>
        )}
      </div>

      <div className="detail-grid">
        {/* Basis Informatie */}
        <section className="detail-section">
          <h2>Basis Informatie</h2>
          <dl>
            <dt>Type</dt>
            <dd>
              <span className={`badge badge-${relation.type.toLowerCase()}`}>
                {relation.type}
              </span>
            </dd>

            <dt>{isParticulier() ? 'Naam' : 'Bedrijfsnaam'}</dt>
            <dd>{getDisplayNaam()}</dd>

            <dt>Contactpersoon</dt>
            <dd>{contactpersoonNaam(relation) || '-'}</dd>

            <dt>Status</dt>
            <dd>
              <span className={`status ${relation.actief ? 'active' : 'inactive'}`}>
                {relation.actief ? 'Actief' : 'Inactief'}
              </span>
            </dd>
          </dl>
        </section>

        {/* Adres */}
        <section className="detail-section">
          <h2>Adres</h2>
          <dl>
            <dt>Straat</dt>
            <dd>
              {relation.straat && relation.huisnummer
                ? `${relation.straat} ${relation.huisnummer}`
                : relation.straat || '-'}
            </dd>

            <dt>Postcode / Plaats</dt>
            <dd>
              {relation.postcode && relation.plaats
                ? `${relation.postcode}, ${relation.plaats}`
                : relation.plaats || '-'}
            </dd>

            <dt>Land</dt>
            <dd>{relation.land}</dd>
          </dl>
        </section>

        {/* Contact */}
        <section className="detail-section">
          <h2>Contact</h2>
          <dl>
            <dt>Telefoon</dt>
            <dd>{relation.telefoon ? <a href={`tel:${relation.telefoon}`}>{relation.telefoon}</a> : '-'}</dd>

            <dt>Mobiel</dt>
            <dd>{relation.mobiel ? <a href={`tel:${relation.mobiel}`}>{relation.mobiel}</a> : '-'}</dd>

            <dt>Email</dt>
            <dd>{relation.email ? <a href={`mailto:${relation.email}`}>{relation.email}</a> : '-'}</dd>

            <dt>Website</dt>
            <dd>
              {relation.website ? (
                <a href={relation.website} target="_blank" rel="noopener noreferrer">
                  {relation.website}
                </a>
              ) : (
                '-'
              )}
            </dd>
          </dl>
        </section>

        {/* Bedrijfsgegevens */}
        <section className="detail-section">
          <h2>Bedrijfsgegevens</h2>
          <dl>
            <dt>KvK Nummer</dt>
            <dd>{relation.kvk_nummer || '-'}</dd>

            <dt>BTW Nummer</dt>
            <dd>{relation.btw_nummer || '-'}</dd>

            <dt>IBAN</dt>
            <dd>{relation.iban || '-'}</dd>

            <dt>Betalingstermijn</dt>
            <dd>{relation.betalingstermijn ? `${relation.betalingstermijn} dagen` : '-'}</dd>

            {relation.kredietlimiet && (
              <>
                <dt>Kredietlimiet</dt>
                <dd>€ {relation.kredietlimiet.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</dd>
              </>
            )}
          </dl>
        </section>

        {/* Notities */}
        {relation.notities && (
          <section className="detail-section span-full">
            <h2>Notities</h2>
            <p className="notities">{relation.notities}</p>
          </section>
        )}

        {/* Klantenportaal */}
        {(relation.type === 'Klant Particulier' || relation.type === 'Klant Bedrijf' || relation.type === 'Beide') && (
          <section className="detail-section span-full">
            <h2>Klantenportaal</h2>
            <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px' }}>
              {portaalTokenInfo?.has_token ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Status:</strong>{' '}
                    <span style={{ color: '#16a34a', fontWeight: 'bold' }}>Actief</span>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Portaal Link:</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}${getBasename()}${portaalTokenInfo.portaal_url}`}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          fontFamily: 'monospace',
                          fontSize: '0.9rem'
                        }}
                      />
                      <button
                        onClick={copyPortaalLink}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: tokenCopied ? '#16a34a' : '#2563eb',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {tokenCopied ? 'Gekopieerd!' : 'Kopieer'}
                      </button>
                    </div>
                  </div>
                  {portaalTokenInfo.laatste_toegang && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Laatste toegang:</strong>{' '}
                      {new Date(portaalTokenInfo.laatste_toegang).toLocaleString('nl-NL')}
                    </div>
                  )}
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Aantal keer bekeken:</strong> {portaalTokenInfo.aantal_keer_bekeken}
                  </div>

                  {/* Wanneer de klant het portaal opende. De teller hierboven telt elke
                      aanroep; deze lijst telt bezoeken - doorklikken binnen een halfuur
                      vanaf hetzelfde adres hoort bij hetzelfde bezoek. */}
                  <div style={{ marginBottom: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => setBezoekenOpen(!bezoekenOpen)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.4rem 0.75rem', border: '1px solid #cbd5e1',
                        borderRadius: '6px', background: '#f8fafc', cursor: 'pointer',
                        fontSize: '0.875rem', color: '#1e293b',
                      }}
                    >
                      <span>{bezoekenOpen ? '\u25bc' : '\u25b6'}</span>
                      <strong>Bezoeken</strong>
                      <span style={{
                        padding: '0.1rem 0.5rem', borderRadius: '0.6rem',
                        background: '#e2e8f0', color: '#475569', fontSize: '0.75rem',
                      }}>{bezoeken.length}</span>
                    </button>

                    {bezoekenOpen && (
                      bezoeken.length === 0 ? (
                        <p style={{ margin: '0.5rem 0 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                          Nog geen bezoeken vastgelegd. Dit wordt bijgehouden sinds 26-09-2026.
                        </p>
                      ) : (
                        <table style={{ width: '100%', marginTop: '0.5rem', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ background: '#f1f3f5' }}>
                              <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Wanneer</th>
                              <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Duur</th>
                              <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Adres</th>
                              <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Browser</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bezoeken.map(b => {
                              const start = new Date(b.begonnen_op);
                              const eind = b.laatste_actie ? new Date(b.laatste_actie) : null;
                              const minuten = eind
                                ? Math.round((eind.getTime() - start.getTime()) / 60000)
                                : 0;
                              return (
                                <tr key={b.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                                  <td style={{ padding: '0.4rem 0.6rem' }}>
                                    {start.toLocaleString('nl-NL')}
                                  </td>
                                  <td style={{ padding: '0.4rem 0.6rem', color: '#64748b' }}>
                                    {minuten > 0 ? `${minuten} min` : '\u2014'}
                                    {b.aantal_aanroepen > 1 && ` (${b.aantal_aanroepen}x geladen)`}
                                  </td>
                                  <td style={{ padding: '0.4rem 0.6rem', color: '#64748b' }}>
                                    {b.ip || '\u2014'}
                                  </td>
                                  <td style={{ padding: '0.4rem 0.6rem', color: '#64748b' }}>
                                    {b.browser ? b.browser.slice(0, 40) : '\u2014'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => window.open(`${window.location.origin}${getBasename()}${portaalTokenInfo.portaal_url}`, '_blank')}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#7c3aed',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Open Portaal
                    </button>
                    <button
                      onClick={handleGenerateToken}
                      disabled={tokenLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#f59e0b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: tokenLoading ? 'not-allowed' : 'pointer',
                        opacity: tokenLoading ? 0.7 : 1
                      }}
                    >
                      {tokenLoading ? 'Bezig...' : 'Nieuwe Link Genereren'}
                    </button>
                    <button
                      onClick={handleRevokeToken}
                      disabled={tokenLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc2626',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: tokenLoading ? 'not-allowed' : 'pointer',
                        opacity: tokenLoading ? 0.7 : 1
                      }}
                    >
                      Link Intrekken
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ marginBottom: '1rem' }}>
                    Genereer een portaal link zodat de klant zijn boten en onderdelen kan bekijken.
                  </p>
                  <button
                    onClick={handleGenerateToken}
                    disabled={tokenLoading}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      cursor: tokenLoading ? 'not-allowed' : 'pointer',
                      opacity: tokenLoading ? 0.7 : 1
                    }}
                  >
                    {tokenLoading ? 'Bezig...' : 'Genereer Portaal Link'}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Boten */}
        <section className="detail-section span-full">
          <h2>Boten ({boten.length})</h2>
          {botenLoading ? (
            <p>Boten laden...</p>
          ) : boten.length === 0 ? (
            <p>Geen boten gevonden voor deze relatie.</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Bootnaam</th>
                    <th>Model</th>
                    <th>Type</th>
                    <th>Bouwjaar</th>
                    <th>Registratienummer</th>
                    <th>Ligplaats</th>
                    <th>Status</th>
                    <th>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {boten.map((boot) => (
                    <tr key={boot.id}>
                      <td>
                        <button
                          type="button"
                          onClick={() => navigate(`/boten/${boot.id}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#4A9EFF',
                            textDecoration: 'underline',
                            font: 'inherit'
                          }}
                        >
                          {boot.bootnaam || '-'}
                        </button>
                      </td>
                      <td>{boot.boot_model.fabrikant} {boot.boot_model.model}</td>
                      <td>{boot.boot_model.type_boot || '-'}</td>
                      <td>{boot.bouwjaar || '-'}</td>
                      <td>{boot.registratienummer || '-'}</td>
                      <td>{boot.ligplaats || '-'}</td>
                      <td>
                        <span
                          className={`status ${boot.eigenaar_id === relationId ? 'active' : 'inactive'} clickable-status`}
                          onClick={() => handleToggleBootEigenaar(boot.id, boot.eigenaar_id === relationId)}
                          style={{ cursor: 'pointer' }}
                          title={boot.eigenaar_id === relationId ? 'Klik om passief te maken' : 'Klik om te reactiveren'}
                        >
                          {boot.eigenaar_id === relationId ? 'Actief' : 'Passief'}
                        </span>
                      </td>
                      <td>
                        {hasPermission('relaties_bewerken') && (
                          <button
                            className="btn-small btn-danger"
                            onClick={() => handleVerwijderBootClick(boot.id, boot.bootnaam || boot.registratienummer || 'deze boot')}
                            title="Verwijder boot van deze klant (boot blijft in systeem)"
                          >
                            Verwijderen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Metadata */}
        <section className="detail-section span-full">
          <h2>Systeem Informatie</h2>
          <dl className="metadata">
            <dt>Aangemaakt op</dt>
            <dd>{new Date(relation.created_at).toLocaleString('nl-NL')}</dd>

            {relation.updated_at && (
              <>
                <dt>Laatst bijgewerkt</dt>
                <dd>{new Date(relation.updated_at).toLocaleString('nl-NL')}</dd>
              </>
            )}
          </dl>
        </section>
      </div>

      <ConfirmDialog
        isOpen={!!confirmDeleteBoot}
        title="Boot Verwijderen van Klant"
        message={`Weet je zeker dat je "${confirmDeleteBoot?.naam}" wilt verwijderen uit de lijst van deze eigenaar? De boot blijft wel in het systeem bestaan.`}
        confirmText="Verwijderen"
        cancelText="Annuleren"
        onConfirm={handleVerwijderBootConfirm}
        onCancel={handleVerwijderBootCancel}
        danger={true}
      />
    </div>
  );
};

export default RelationDetail;